/**
 * Customer password rules — a mirror of the prod Cognito pool's PasswordPolicy
 * (`us-east-1_dJMJiGF49`: min 8, upper, lower, number, symbol).
 *
 * Cognito is the real enforcer; this exists so customers see every rule up
 * front instead of discovering the symbol requirement from a rejection. The
 * forms used to say only "at least 8 characters" and the rejection message
 * left out symbols, so people who followed the instructions still failed.
 * If the pool policy changes, change it here too.
 *
 * Dependency-free on purpose: it is imported by client forms and server routes.
 */

/**
 * Cognito's accepted special characters. A space counts too, but only inside
 * the password, so the symbol test runs on the trimmed value.
 */
const SYMBOL = /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]/;

export interface PasswordRule {
  id: 'length' | 'upper' | 'lower' | 'number' | 'symbol';
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: readonly PasswordRule[] = [
  { id: 'length', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { id: 'upper', label: 'An uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lower', label: 'A lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { id: 'number', label: 'A number', test: (pw) => /[0-9]/.test(pw) },
  { id: 'symbol', label: 'A symbol, like ! @ # $', test: (pw) => SYMBOL.test(pw.trim()) },
];

export const PASSWORD_REQUIREMENTS_MESSAGE =
  'Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a symbol (like ! @ # $).';

export function meetsPasswordPolicy(password: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(password));
}
